import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileText, ClipboardList, Mail } from "lucide-react";
import api from "@/lib/api";
import { eur, COMMERCIAL_LABEL, SITE_LABEL } from "@/lib/format";
import DocumentsPanel from "@/pages/DocumentsPanel";
import ProjectSheet from "@/components/chantiers/ProjectSheet";
import ClientCard from "@/components/chantiers/ClientCard";
import ProjectCommunications from "@/components/chantiers/ProjectCommunications";

export default function ProjectDetail({ projectId, onBack }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("docs");
  const load = useCallback(async () => { try { const { data } = await api.get(`/chantiers/${projectId}/full`); setD(data); } catch { toast.error("Projet introuvable"); } }, [projectId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-sm text-ink-soft">Chargement du projet…</div>;
  const p = d.project;
  const st = Object.values(d.states)[0];
  const client = p.contact?.nom ? `${p.contact.prenom || ""} ${p.contact.nom}`.trim() : "Prospect";
  const issued = d.invoices.filter(i => i.status !== "brouillon");

  return (
    <div data-testid="project-detail" className="space-y-5">
      <button onClick={onBack} data-testid="project-back" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-anthracite"><ArrowLeft size={14} /> Tous les projets</button>

      <div className="bg-anthracite text-ivory rounded-rama-md p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono-caption text-greige">{p.ref}</div>
            <h1 className="font-display text-2xl md:text-4xl truncate">{client}</h1>
            <div className="text-sm text-ivory/70 mt-1 truncate">{p.type_projet || p.description?.slice(0, 80) || "Projet"}{p.contact?.ville ? ` · ${p.contact.ville}` : ""}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="text-xs px-2.5 py-1 rounded-full bg-ivory/10 text-ivory" data-testid="detail-commercial-status">{COMMERCIAL_LABEL[p.commercial_status || p.status] || p.status}</span>
              {p.site_status && <span className="text-xs px-2.5 py-1 rounded-full bg-bordeaux-500/40 text-ivory" data-testid="detail-site-status">{SITE_LABEL[p.site_status]}</span>}
              {p.progress_pct != null && <span className="text-xs px-2.5 py-1 rounded-full bg-ivory/10 text-ivory">Avancement {p.progress_pct} %</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:text-right">
            {[["Marché", st?.marche_ttc], ["Facturé", st?.facture_ttc], ["Encaissé", st?.paye], ["Reste à facturer", st?.reste_a_facturer_ttc]].map(([l, v]) => (
              <div key={l}><div className="text-xs text-greige">{l}</div><div className="font-display text-lg md:text-xl">{st ? eur(v) : "—"}</div></div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 text-center text-xs text-ivory/80">
          <div className="bg-ivory/5 rounded-rama-sm py-2">{d.quotes.length} devis</div>
          <div className="bg-ivory/5 rounded-rama-sm py-2">{issued.length} facture(s)</div>
          <div className="bg-ivory/5 rounded-rama-sm py-2">{d.documents.length} document(s)</div>
        </div>
      </div>

      <ClientCard project={p} onSaved={load} />

      <div className="flex gap-1 border-b border-black/10 overflow-x-auto">
        {[{ v: "docs", l: "Devis & factures", i: FileText }, { v: "suivi", l: "Suivi du chantier", i: ClipboardList }, { v: "comms", l: "Communications", i: Mail }].map(t => (
          <button key={t.v} data-testid={`detail-tab-${t.v}`} onClick={() => { setTab(t.v); window.scrollTo({ top: 0, behavior: "instant" }); }} className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 whitespace-nowrap border-b-2 -mb-px ${tab === t.v ? "border-bordeaux-700 text-bordeaux-700" : "border-transparent text-ink-soft hover:text-anthracite"}`}><t.i size={14} /> {t.l}</button>
        ))}
      </div>

      {tab === "docs" && <DocumentsPanel key={projectId} projectId={projectId} project={p} />}
      {tab === "suivi" && <ProjectSheet projectId={projectId} inline onClose={() => {}} />}
      {tab === "comms" && <ProjectCommunications projectId={projectId} />}
    </div>
  );
}
